import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  Plus,
  MapPin,
  BedDouble,
  MoreVertical,
  Pencil,
  Trash2,
  FileText,
  AlertCircle,
  Upload,
  Image,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

export default function PropertiesPage() {
  const { getAuthHeader } = useAuth();
  const [properties, setProperties] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    total_rooms: "",
    property_type: "Hotel",
    hero_image: "",
  });
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [propertiesRes, budgetsRes] = await Promise.all([
        axios.get(`${API}/properties`, { headers: getAuthHeader() }),
        axios.get(`${API}/budgets`, { headers: getAuthHeader() }),
      ]);
      setProperties(propertiesRes.data);
      setBudgets(budgetsRes.data);
    } catch (error) {
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        total_rooms: parseInt(formData.total_rooms),
      };

      if (editingProperty) {
        await axios.put(
          `${API}/properties/${editingProperty.id}`,
          payload,
          { headers: getAuthHeader() }
        );
        toast.success("Property updated successfully");
      } else {
        await axios.post(`${API}/properties`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Property created successfully");
      }

      setDialogOpen(false);
      setEditingProperty(null);
      setFormData({ name: "", location: "", total_rooms: "", property_type: "Hotel", hero_image: "" });
      setImagePreview(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save property");
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      name: property.name,
      location: property.location,
      total_rooms: property.total_rooms.toString(),
      property_type: property.property_type,
      hero_image: property.hero_image || "",
    });
    setImagePreview(property.hero_image || null);
    setDialogOpen(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, hero_image: reader.result });
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDelete = async (propertyId) => {
    if (!confirm("Are you sure you want to delete this property? This will also delete all associated budgets.")) {
      return;
    }
    try {
      await axios.delete(`${API}/properties/${propertyId}`, {
        headers: getAuthHeader(),
      });
      toast.success("Property deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete property");
    }
  };

  const getPropertyBudget = (propertyId) => {
    return budgets.find((b) => b.property_id === propertyId);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="properties-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1
              className="text-3xl font-bold text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Properties
            </h1>
            <p className="text-slate-500 mt-1">
              Manage your hotel properties and create budgets
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-slate-900 hover:bg-slate-800 btn-transition"
                onClick={() => {
                  setEditingProperty(null);
                  setFormData({ name: "", location: "", total_rooms: "", property_type: "Hotel", hero_image: "" });
                  setImagePreview(null);
                }}
                data-testid="add-property-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Property
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>
                  {editingProperty ? "Edit Property" : "Add New Property"}
                </DialogTitle>
                <DialogDescription>
                  {editingProperty
                    ? "Update the property details below"
                    : "Enter the details for your new property"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Hero Image Upload */}
                <div className="space-y-2">
                  <Label>Hero Image (for Excel cover page)</Label>
                  <div className="flex items-center gap-4">
                    {imagePreview ? (
                      <div className="relative w-32 h-20 rounded-lg overflow-hidden border border-slate-200">
                        <img
                          src={imagePreview}
                          alt="Property hero"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, hero_image: "" });
                            setImagePreview(null);
                          }}
                          className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-32 h-20 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50">
                        <Upload className="w-5 h-5 text-slate-400" />
                        <span className="text-xs text-slate-400 mt-1">Upload</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageUpload}
                          data-testid="hero-image-input"
                        />
                      </label>
                    )}
                    <div className="flex-1 text-xs text-slate-500">
                      <p>Upload a hero image of your property.</p>
                      <p>This will appear on the Excel budget cover page.</p>
                      <p className="text-amber-600">Max 5MB, JPG/PNG</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Property Name</Label>
                  <Input
                    id="name"
                    placeholder="Grand Hotel Sydney"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    data-testid="property-name-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    placeholder="Melbourne, VIC"
                    value={formData.location}
                    onChange={(e) =>
                      setFormData({ ...formData, location: e.target.value })
                    }
                    data-testid="property-location-input"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="total_rooms">Number of Rooms</Label>
                    <Input
                      id="total_rooms"
                      type="number"
                      min="1"
                      placeholder="50"
                      value={formData.total_rooms}
                      onChange={(e) =>
                        setFormData({ ...formData, total_rooms: e.target.value })
                      }
                      data-testid="property-rooms-input"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="property_type">Property Type</Label>
                    <Select
                      value={formData.property_type}
                      onValueChange={(value) =>
                        setFormData({ ...formData, property_type: value })
                      }
                    >
                      <SelectTrigger data-testid="property-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Hotel">Hotel</SelectItem>
                        <SelectItem value="Apartment">Serviced Apartment</SelectItem>
                        <SelectItem value="Resort">Resort</SelectItem>
                        <SelectItem value="Motel">Motel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter className="gap-2 sm:gap-0">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid="property-submit-btn"
                  >
                    {editingProperty ? "Update" : "Create"} Property
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Properties Grid */}
        {properties.length === 0 ? (
          <Card className="border-slate-200 border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <Building2 className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                No properties yet
              </h3>
              <p className="text-slate-500 text-center max-w-sm mb-4">
                Add your first property to start creating budgets and cash flow
                projections.
              </p>
              <Button
                className="bg-amber-600 hover:bg-amber-700"
                onClick={() => setDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Property
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property, index) => {
              const budget = getPropertyBudget(property.id);
              return (
                <Card
                  key={property.id}
                  className="property-card border-slate-200 overflow-hidden animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`property-card-${index}`}
                >
                  <div className="h-36 bg-gradient-to-br from-slate-800 to-slate-900 relative">
                    <img
                      src={
                        index % 2 === 0
                          ? "https://images.unsplash.com/photo-1765439178218-e54dcbb64bcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3RlbCUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlJTIwbmlnaHR8ZW58MHx8fHwxNzcwNjA4MTcwfDA&ixlib=rb-4.1.0&q=85"
                          : "https://images.unsplash.com/photo-1769766407883-1645a93eed40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3RlbCUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlJTIwbmlnaHR8ZW58MHx8fHwxNzcwNjA4MTcwfDA&ixlib=rb-4.1.0&q=85"
                      }
                      alt={property.name}
                      className="w-full h-full object-cover opacity-60"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                    <div className="absolute top-3 right-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-white hover:bg-white/20"
                            data-testid={`property-menu-${index}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(property)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDelete(property.id)}
                            data-testid={`delete-property-${index}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="absolute bottom-3 left-4 right-4">
                      <Badge className="bg-white/20 text-white text-xs mb-2">
                        {property.property_type}
                      </Badge>
                      <h3 className="text-lg font-bold text-white truncate">
                        {property.name}
                      </h3>
                      <div className="flex items-center gap-1 text-slate-300 text-sm">
                        <MapPin className="w-3 h-3" />
                        {property.location}
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <BedDouble className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-600">
                          {property.total_rooms} rooms
                        </span>
                      </div>
                      {budget ? (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          FY {budget.fiscal_year_start}
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-amber-600 border-amber-300"
                        >
                          <AlertCircle className="w-3 h-3 mr-1" />
                          No Budget
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/properties/${property.id}`} className="flex-1">
                        <Button variant="outline" className="w-full" size="sm">
                          View Details
                        </Button>
                      </Link>
                      {budget ? (
                        <Link to={`/reports/${budget.id}`}>
                          <Button
                            size="sm"
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid={`view-reports-${index}`}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                        </Link>
                      ) : (
                        <Link to={`/budget/${property.id}`}>
                          <Button
                            size="sm"
                            className="bg-slate-900 hover:bg-slate-800"
                            data-testid={`create-budget-${index}`}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
